import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Play,
  Pause,
  Settings,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Activity,
  ChevronDown,
  ChevronUp,
  Calendar,
  Target,
  Zap,
  BarChart2,
  AlertTriangle,
  Info
} from 'lucide-react';
import { useBots } from '@/contexts/BotsContext';
import { useAuth } from '../contexts/AuthContext';
import { Trade } from '../utils/types';
import { cn } from '../utils/cn';
import { useApiKeys } from '../hooks/useApiKeys';
import { Button } from '../ui/Button';
import { fetchBacktestsByBotId } from '@/lib/db/backtests';

interface BacktestResult {
  id: string;
  total_profit: number;
  total_profit_amount: number;
  quote_currency: string;
  total_trades: number;
  win_rate: number;
  max_drawdown: number;
  sharpe_ratio: number;
  trade_history?: Trade[];
  chart_data?: any;
  created_at: string;
  has_warnings?: boolean;
  feedback?: string;
  error_message?: string;
  status?: string;
}

export const BotDetailsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bots, toggleBotStatus } = useBots();
  const { apiKeys } = useApiKeys();
  const [expandedTrades, setExpandedTrades] = useState<Record<string, boolean>>({});
  const [botBacktests, setBotBacktests] = useState<any[]>([]);
  const [loadingBacktests, setLoadingBacktests] = useState(true);

  const bot = bots.find(b => b.id === id);
  const botApiKeys = apiKeys.filter(key => key.bot_id === id);
  const latestBacktest = botBacktests.sort((a, b) =>
    new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  )[0];

  // Fetch backtests for this bot
  useEffect(() => {
    if (!id) return;

    async function loadBacktests() {
      try {
        setLoadingBacktests(true);
        const data = await fetchBacktestsByBotId(id);
        setBotBacktests(data);
      } catch (error) {
        console.error('[BOT DETAILS] Error loading backtests:', error);
      } finally {
        setLoadingBacktests(false);
      }
    }

    loadBacktests();
  }, [id]);

  if (!bot) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Bot Not Found</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">The bot you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/dashboard')}>
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  const formatProfit = (profit: number) => {
    const sign = profit >= 0 ? '+' : '';
    return `${sign}${profit.toFixed(2)}%`;
  };

  const formatCurrency = (amount: number, currency: string = 'USDT') => {
    return `${amount.toFixed(2)} ${currency}`;
  };

  const toggleTradeExpansion = (tradeId: string) => {
    setExpandedTrades(prev => ({
      ...prev,
      [tradeId]: !prev[tradeId]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back</span>
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{bot.name}</h1>
              <p className="text-gray-600 dark:text-gray-400">{bot.description}</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={() => toggleBotStatus(bot.id)}
              className={cn(
                "flex items-center space-x-2",
                bot.is_active 
                  ? "bg-red-600 hover:bg-red-700 text-white" 
                  : "bg-green-600 hover:bg-green-700 text-white"
              )}
            >
              {bot.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{bot.is_active ? 'Stop' : 'Start'}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate(`/bots/${bot.id}/edit`)}
              className="flex items-center space-x-2"
            >
              <Settings className="w-4 h-4" />
              <span>Settings</span>
            </Button>
          </div>
        </div>

        {/* Bot Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Status</p>
                <p className={cn(
                  "text-lg font-semibold",
                  bot.is_active ? "text-green-600" : "text-gray-500"
                )}>
                  {bot.is_active ? 'Active' : 'Inactive'}
                </p>
              </div>
              <Activity className={cn(
                "w-8 h-8",
                bot.is_active ? "text-green-600" : "text-gray-400"
              )} />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Profit</p>
                <p className={cn(
                  "text-lg font-semibold",
                  (bot.profit || 0) >= 0 ? "text-green-600" : "text-red-600"
                )}>
                  {formatProfit(bot.profit || 0)}
                </p>
              </div>
              {(bot.profit || 0) >= 0 ? (
                <TrendingUp className="w-8 h-8 text-green-600" />
              ) : (
                <TrendingDown className="w-8 h-8 text-red-600" />
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Trades</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {bot.total_trades || 0}
                </p>
              </div>
              <BarChart2 className="w-8 h-8 text-blue-600" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Win Rate</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-white">
                  {/* NOTE: bot.win_rate is PERCENTAGE (73.75) */}
                  {bot.win_rate ? bot.win_rate.toFixed(1) : '0.0'}%
                </p>
              </div>
              <Target className="w-8 h-8 text-purple-600" />
            </div>
          </motion.div>
        </div>

        {/* Bot Configuration */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configuration</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Exchange:</span>
                <span className="font-medium text-gray-900 dark:text-white">{bot.exchange}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Strategy:</span>
                <span className="font-medium text-gray-900 dark:text-white">{bot.strategy}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Quote Currency:</span>
                <span className="font-medium text-gray-900 dark:text-white">{bot.quote_currency || 'USDT'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">Practice Mode:</span>
                <span className={cn(
                  "font-medium",
                  bot.is_practice ? "text-blue-600" : "text-green-600"
                )}>
                  {bot.is_practice ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">API Keys</h3>
            {botApiKeys.length > 0 ? (
              <div className="space-y-3">
                {botApiKeys.map((apiKey) => (
                  <div key={apiKey.id} className="flex justify-between items-center">
                    <span className="text-gray-600 dark:text-gray-400">{apiKey.exchange_name}:</span>
                    <span className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      apiKey.status === 'connected' 
                        ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                        : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                    )}>
                      {apiKey.status}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <AlertTriangle className="w-8 h-8 text-yellow-500 mx-auto mb-2" />
                <p className="text-gray-600 dark:text-gray-400">No API keys configured</p>
                <Link
                  to="/api-keys"
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                >
                  Configure API Keys
                </Link>
              </div>
            )}
          </motion.div>
        </div>

        {/* Latest Backtest Results */}
        {latestBacktest && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Latest Backtest Results</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>{new Date(latestBacktest.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            {latestBacktest.status === 'running' && (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600 dark:text-gray-400">Backtest running...</span>
              </div>
            )}

            {latestBacktest.status === 'completed' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Profit</p>
                    <p className={cn(
                      "text-xl font-bold",
                      latestBacktest.total_profit >= 0 ? "text-green-600" : "text-red-600"
                    )}>
                      {formatProfit(latestBacktest.total_profit)}
                    </p>
                    {latestBacktest.net_profit !== undefined && (
                      <p className={cn(
                        "text-sm",
                        latestBacktest.net_profit >= 0 ? "text-green-500" : "text-red-500"
                      )}>
                        Net: {formatProfit(latestBacktest.net_profit)}
                      </p>
                    )}
                    <p className="text-sm text-gray-500">
                      {formatCurrency(latestBacktest.total_profit_amount, latestBacktest.quote_currency)}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Total Trades</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {latestBacktest.total_trades}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Win Rate</p>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {/* NOTE: win_rate_pct is PERCENTAGE (73.75) */}
                      {(latestBacktest.win_rate_pct || latestBacktest.win_rate || 0).toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm text-gray-600 dark:text-gray-400">Max Drawdown</p>
                    <p className="text-xl font-bold text-red-600">
                      {latestBacktest.max_drawdown.toFixed(2)}%
                    </p>
                    {latestBacktest.total_fees !== undefined && (
                      <p className="text-xs text-gray-500">
                        Total Fees: {formatCurrency(latestBacktest.total_fees, latestBacktest.quote_currency)}
                      </p>
                    )}
                  </div>
                </div>

                {latestBacktest.has_warnings && latestBacktest.feedback && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                      <div>
                        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                          Backtest Warnings
                        </h4>
                        <p className="text-sm text-yellow-700 dark:text-yellow-300 mt-1">
                          {latestBacktest.feedback}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trade History */}
                {latestBacktest.trade_history && latestBacktest.trade_history.length > 0 && (
                  <div>
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white mb-4">Trade History</h4>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead className="bg-gray-50 dark:bg-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Pair
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Type
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Entry Price
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Exit Price
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Quantity
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Profit %
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Fee
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Mode
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Reason
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                          {latestBacktest.trade_history.map((trade, index) => (
                            <React.Fragment key={`${trade.pair}-${trade.timestamp}-${index}`}>
                              <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                                  {trade.pair}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={cn(
                                    "px-2 py-1 rounded-full text-xs font-medium",
                                    trade.type === 'buy' 
                                      ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                                      : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                                  )}>
                                    {trade.type.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  ${trade.entry_price?.toFixed(6) || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  ${trade.exit_price?.toFixed(6) || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                  {trade.quantity?.toFixed(6) || 'N/A'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                  <span className={cn(
                                    "font-medium",
                                    (trade.profit_percentage || 0) >= 0 ? "text-green-600" : "text-red-600"
                                  )}>
                                    {formatProfit(trade.profit_percentage || 0)}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                                  {trade.fee !== undefined ? formatCurrency(trade.fee, trade.quote_currency || latestBacktest.quote_currency) : '-'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    trade.mode === 'Backtest' 
                                      ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' 
                                      : trade.mode === 'Live'
                                      ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                                      : 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400'
                                  }`}>
                                    {trade.mode || 'Backtest'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  {trade.explanation ? (
                                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                      trade.explanation.includes('🟩') ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400' :
                                      trade.explanation.includes('🟦') ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400' :
                                      trade.explanation.includes('🟥') ? 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400' :
                                      trade.explanation.includes('🟨') ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400' :
                                      'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                    }`}>
                                      {trade.explanation}
                                    </span>
                                  ) : (
                                    <span className="text-sm text-gray-500 dark:text-gray-400">-</span>
                                  )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                  <button
                                    onClick={() => toggleTradeExpansion(`${trade.pair}-${trade.timestamp}-${index}`)}
                                    className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                                  >
                                    {expandedTrades[`${trade.pair}-${trade.timestamp}-${index}`] ? (
                                      <ChevronUp className="w-4 h-4" />
                                    ) : (
                                      <ChevronDown className="w-4 h-4" />
                                    )}
                                    <span>Details</span>
                                  </button>
                                </td>
                              </tr>
                              {expandedTrades[`${trade.pair}-${trade.timestamp}-${index}`] && (
                                <tr>
                                  <td colSpan={8} className="px-6 py-4 bg-gray-50 dark:bg-gray-700">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                      <div>
                                        <span className="font-medium text-gray-600 dark:text-gray-300">Timestamp:</span>
                                        <p className="text-gray-900 dark:text-white">
                                          {new Date(trade.timestamp).toLocaleString()}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-600 dark:text-gray-300">Profit Amount:</span>
                                        <p className={cn(
                                          "font-medium",
                                          (trade.profit_amount || 0) >= 0 ? "text-green-600" : "text-red-600"
                                        )}>
                                          {formatCurrency(trade.profit_amount || 0, trade.quote_currency || latestBacktest.quote_currency)}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-600 dark:text-gray-300">Fee:</span>
                                        <p className="text-gray-900 dark:text-white">
                                          {formatCurrency(trade.fee || 0, trade.quote_currency || latestBacktest.quote_currency)}
                                        </p>
                                      </div>
                                      <div>
                                        <span className="font-medium text-gray-600 dark:text-gray-300">Total:</span>
                                        <p className="text-gray-900 dark:text-white">
                                          {formatCurrency(trade.total || 0, latestBacktest.quote_currency)}
                                        </p>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </>
            )}

            {latestBacktest.status === 'error' && latestBacktest.error_message && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Backtest Error
                    </h4>
                    <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                      {latestBacktest.error_message}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* All Backtest Results */}
        {botBacktests.length > 1 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6"
          >
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">All Backtest Results</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Total Profit
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Trades
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Win Rate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {botBacktests.map((result) => (
                    <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {new Date(result.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={cn(
                          "px-2 py-1 rounded-full text-xs font-medium",
                          result.status === 'completed' 
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : result.status === 'running'
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        )}>
                          {result.status || 'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={cn(
                          "font-medium",
                          result.total_profit >= 0 ? "text-green-600" : "text-red-600"
                        )}>
                          {formatProfit(result.total_profit)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {result.total_trades}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {/* NOTE: win_rate_pct is PERCENTAGE (73.75) */}
                        {(result.win_rate_pct || result.win_rate || 0).toFixed(1)}%
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <Link
                          to={`/backtests/${result.id}`}
                          className="text-blue-600 hover:text-blue-700 flex items-center space-x-1"
                        >
                          <Info className="w-4 h-4" />
                          <span>View Details</span>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};